// # Calendar Fullscreen Modal Component
// Fullscreen calendar with close button

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { CalendarMedium } from './CalendarMedium';
import { CalendarEventForm } from './CalendarEventForm';
import { CalendarFilterToggle, type CalendarFilterType } from './CalendarFilterToggle';
import type { CalendarEvent, CalendarEventFormData, CalendarViewType } from '../../types/calendar';
import { CalendarService } from '../../lib/calendar-service';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';

interface CalendarFullscreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  departments?: Array<{ id: string; name: string }>;
  departmentId?: string;
  departmentName?: string;
  defaultFilter?: CalendarFilterType;
  showAllOption?: boolean;
}

export function CalendarFullscreenModal({
  isOpen,
  onClose,
  userId,
  departments = [],
  departmentId,
  departmentName,
  defaultFilter,
  showAllOption = false
}: CalendarFullscreenModalProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]); // Store all loaded events
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewType, setViewType] = useState<CalendarViewType>('month');
  const [filter, setFilter] = useState<CalendarFilterType>(
    defaultFilter || (departmentId ? 'department' : 'my')
  );
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const { isMobile } = useDeviceInfo();

  // # Determine if department filter should be shown
  const showDepartmentFilter = !!departmentId && !!departmentName;

  // # Load events with proper error handling
  const loadEvents = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError('User ID is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0);

      // Load all events the user has access to
      const result = await CalendarService.getUserCalendar(
        userId,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        true, // includePrivate
        true, // includeDepartment
        true  // includeGlobal
      );

      if (result.success) {
        setAllEvents(result.data || []);
        setError(null);
      } else {
        setError(result.error || 'Failed to load calendar events');
        setAllEvents([]);
        console.error('Error loading events:', result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      setAllEvents([]);
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // # Filter events based on selected filter
  useEffect(() => {
    if (!allEvents.length) {
      setEvents([]);
      return;
    }

    let filtered: CalendarEvent[] = [];

    switch (filter) {
      case 'all':
        // Show all events user is directly linked to (already loaded via getUserCalendar)
        // This includes: private events (created by user or user is participant),
        // department events (user is in that department), and global events
        filtered = allEvents;
        break;
      
      case 'my':
        // Show only events the user has created themselves (regardless of visibility)
        filtered = allEvents.filter(event => 
          event.created_by === userId
        );
        break;
      
      case 'my_department':
        // Show all user's calendar items associated to the department
        // This includes all department events for this department (they have access to)
        if (departmentId) {
          filtered = allEvents.filter(event => 
            event.visibility === 'department' && event.department_id === departmentId
          );
        } else {
          filtered = [];
        }
        break;
      
      case 'department':
        // Show all department associated items (all department visibility events for this department)
        if (departmentId) {
          filtered = allEvents.filter(event => 
            event.visibility === 'department' && event.department_id === departmentId
          );
        } else {
          filtered = [];
        }
        break;
      
      default:
        filtered = allEvents;
    }

    setEvents(filtered);
  }, [filter, allEvents, userId, departmentId]);

  // # Load events when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      loadEvents();
    } else if (!isOpen) {
      // Reset state when modal closes
      setEvents([]);
      setAllEvents([]);
      setError(null);
      setLoading(false);
      // Reset filter to default
      setFilter(defaultFilter || (departmentId ? 'department' : 'my'));
    }
  }, [isOpen, userId, loadEvents, defaultFilter, departmentId]);

  // # Handle event creation
  const handleCreateEvent = (date?: Date) => {
    setSelectedDate(date || new Date());
    setSelectedEvent(null);
    setShowEventForm(true);
  };

  // # Handle event click
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventForm(true);
  };

  // # Handle form submit
  const handleFormSubmit = async (data: CalendarEventFormData) => {
    try {
      if (selectedEvent) {
        // Update existing event
        const result = await CalendarService.updateEvent(selectedEvent.id, data);
        if (result.success) {
          await loadEvents();
          setShowEventForm(false);
          setSelectedEvent(null);
        }
      } else {
        // Create new event
        const result = await CalendarService.createEvent(data);
        if (result.success) {
          await loadEvents();
          setShowEventForm(false);
          setSelectedDate(null);
        }
      }
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  // # Handle form cancel
  const handleFormCancel = () => {
    setShowEventForm(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  // # Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showEventForm) {
          handleFormCancel();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showEventForm, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-[9999] flex flex-col w-screen h-screen">
        {/* Header - Mobile optimized */}
        <div className={`flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 ${
          isMobile ? 'p-2' : 'p-4'
        }`}>
          {/* Top row: Title and Close button */}
          <div className="flex items-center justify-between mb-2">
            <h2 className={`font-semibold text-gray-900 dark:text-gray-100 ${
              isMobile ? 'text-base' : 'text-xl'
            }`}>
              Calendar
            </h2>
            <button
              onClick={onClose}
              className={`text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ${
                isMobile ? 'p-1.5' : 'p-2'
              }`}
            >
              <X className={isMobile ? 'w-5 h-5' : 'w-6 h-6'} />
            </button>
          </div>

          {/* Bottom row: Filters and View Selector - Mobile optimized */}
          {!showEventForm && (
            <div className={`flex items-center gap-2 ${
              isMobile ? 'flex-wrap' : ''
            }`}>
              {/* Filter Toggle - Scrollable on mobile if needed */}
              <div className={isMobile ? 'flex-1 min-w-0 overflow-x-auto' : ''}>
                <CalendarFilterToggle
                  filter={filter}
                  onChange={setFilter}
                  departmentName={departmentName}
                  showDepartment={showDepartmentFilter}
                  showAll={showAllOption}
                />
              </div>
              
              {/* View Type Selector */}
              <div className={`flex items-center bg-gray-100 rounded-lg p-1 ${
                isMobile ? 'flex-shrink-0 overflow-x-auto' : 'flex-wrap'
              }`}>
                <button
                  onClick={() => setViewType('day')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'day'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Day
                </button>
                <button
                  onClick={() => setViewType('week')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'week'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewType('month')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'month'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setViewType('list')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'list'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  List
                </button>
                <button
                  onClick={() => setViewType('city')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'city'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="City view - Zoomed in view"
                >
                  City
                </button>
                <button
                  onClick={() => setViewType('3d')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === '3d'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="3D view - Interactive 3D perspective"
                >
                  3D
                </button>
                <button
                  onClick={() => setViewType('timeline')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'timeline'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Timeline view - Horizontal timeline"
                >
                  Timeline
                </button>
                <button
                  onClick={() => setViewType('heatmap')}
                  className={`font-medium rounded-md transition-colors ${
                    isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                  } ${
                    viewType === 'heatmap'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Heatmap view - Activity density visualization"
                >
                  Heatmap
                </button>
              </div>
            </div>
          )}

          {/* New Event Button - Mobile: Show as FAB or in header */}
          {!showEventForm && !isMobile && (
            <div className="flex items-center justify-end mt-2">
              <button
                onClick={() => handleCreateEvent()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                New Event
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-auto ${
          isMobile ? 'p-2' : 'p-6'
        }`}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500">Loading calendar events...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4 max-w-md text-center">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-red-800 font-medium">Error loading calendar</p>
                  <p className="text-red-600 text-sm mt-1">{error}</p>
                </div>
                <button
                  onClick={loadEvents}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : showEventForm ? (
            <div className="max-w-3xl mx-auto">
              <CalendarEventForm
                event={selectedEvent || undefined}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                departments={departments}
              />
            </div>
          ) : (
            <CalendarMedium
              events={events}
              viewType={viewType}
              onEventClick={handleEventClick}
              onCreateEvent={handleCreateEvent}
              className="h-full"
              departments={departments}
              userId={userId}
            />
          )}
        </div>
    </div>
  );
}

export default CalendarFullscreenModal;

